import { push } from 'react-router-redux';
import unescaper from 'anchor-server-common/utilities/unescaper';

export const FETCHING_SEGMENTS = '@@partnerAnalytics/FETCHING_SEGMENTS';
export const RECEIVE_BIO = '@@partnerAnalytics/RECEIVE_BIO';
export const RECEIVE_EPISODE_COUNT = '@@partnerAnalytics/RECEIVE_EPISODE_COUNT';
export const RECEIVE_FOLLOWER_COUNT =
  '@@partnerAnalytics/RECEIVE_FOLLOWER_COUNT';
export const RECEIVE_SEGMENTS = '@@partnerAnalytics/RECEIVE_SEGMENTS';
export const RECEIVE_TOTAL_PLAY_COUNT =
  '@@partnerAnalytics/RECEIVE_TOTAL_PLAY_COUNT';
export const REQUEST_TRANSCRIPTION = '@@partnerAnalytics/REQUEST_TRANSCRIPTION';

const emptyArray = [];
const emptyObject = {};

const initialState = {
  bio: null,
  followerCount: null,
  isFetching: false,
  segments: emptyArray,
  totalPlayCount: null,
};

// reducers

function segmentReducer(state = emptyObject, action) {
  switch (action.type) {
    case RECEIVE_SEGMENTS: {
      const { caption } = state;
      if (caption) {
        return {
          ...state,
          caption: unescaper(caption),
        };
      }
      return state;
    }
    case REQUEST_TRANSCRIPTION: {
      const { audioId, transcriptionRequestedTime } = action;
      if (state.audioId !== audioId) {
        return state;
      }
      return {
        ...state,
        transcriptionRequestStatus: 'pending',
      };
    }
    default:
      return state;
  }
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case FETCHING_SEGMENTS: {
      const { isFetching } = action;
      return {
        ...state,
        isFetching,
      };
    }
    case RECEIVE_SEGMENTS:
      return {
        ...state,
        segments: action.segments.map(s => segmentReducer(s, action)),
      };
    case RECEIVE_BIO:
      return {
        ...state,
        bio: unescaper(action.bio),
      };
    case RECEIVE_TOTAL_PLAY_COUNT:
      return {
        ...state,
        totalPlayCount: action.totalPlayCount,
      };
    case RECEIVE_EPISODE_COUNT:
      return {
        ...state,
        episodeCount: action.episodeCount,
      };
    case RECEIVE_FOLLOWER_COUNT:
      return {
        ...state,
        followerCount: action.followerCount,
      };
    case REQUEST_TRANSCRIPTION: {
      const { segments } = state;
      return {
        ...state,
        segments: segments.map(s => segmentReducer(s, action)),
      };
    }
    default:
      return state;
  }
}

// action creators

function receiveBio(bio) {
  return {
    type: RECEIVE_BIO,
    bio,
  };
}

function receiveTotalPlayCount(totalPlayCount) {
  return {
    type: RECEIVE_TOTAL_PLAY_COUNT,
    totalPlayCount,
  };
}

function receiveEpisodeCount(episodeCount) {
  return {
    type: RECEIVE_EPISODE_COUNT,
    episodeCount,
  };
}

function receiveFollowerCount(followerCount) {
  return {
    type: RECEIVE_FOLLOWER_COUNT,
    followerCount,
  };
}

function receiveSegments(segments) {
  return {
    type: RECEIVE_SEGMENTS,
    segments,
  };
}

function fetchingSegments(isFetching) {
  return {
    type: FETCHING_SEGMENTS,
    isFetching,
  };
}

function requestTranscription(audioId) {
  return {
    type: REQUEST_TRANSCRIPTION,
    audioId,
  };
}
// thunks

export function fetchBio() {
  return (dispatch, getState) =>
    fetch('/api/user/bio', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        const { bio } = responseJson;
        if (bio) {
          dispatch(receiveBio(bio));
        }
      });
}

export function fetchEpisodeCount() {
  return (dispatch, getState) =>
    fetch('/api/user/episodecount', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        const { episodeCount } = responseJson;
        dispatch(receiveEpisodeCount(episodeCount));
      });
}

export function fetchFollowerCount() {
  return (dispatch, getState) =>
    fetch('/api/user/followercount', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        const { followerCount } = responseJson;
        dispatch(receiveFollowerCount(followerCount));
      });
}

export function fetchTotalPlayCount() {
  return (dispatch, getState) =>
    fetch('/api/user/playcount', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        const { totalPlayCount } = responseJson;
        dispatch(receiveTotalPlayCount(totalPlayCount));
      });
}

export function fetchSegments() {
  return (dispatch, getState) => {
    dispatch(fetchingSegments(true));
    return fetch('/api/user/segments', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            return dispatch(push('/login'));
          }
          // e.g. no permissions (403)
          return dispatch(push('/404'));
        }
        const { segments } = responseJson;
        dispatch(receiveSegments(segments));
        return segments;
      })
      .then(always, always);

    function always(result) {
      dispatch(fetchingSegments(false));
      return result; // for continuing promise chain
    }
  };
}

export function fetchRequestTranscription(audioId) {
  return (dispatch, getState) => {
    const {
      partnerAnalytics: { segments },
    } = getState();
    const segment = segments.find(s => s.audioId === audioId);
    const caption = segment && segment.caption;
    return fetch(`/api/user/audios/${audioId}/transcriptionrequest`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        caption,
      }),
    }).then(response => {
      if (response.status === 200) {
        dispatch(requestTranscription(audioId));
      }
    });
  };
}

export function clickPartnerAnalytics(target) {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-partner-analytics-click',
        payload: {
          target,
        },
      },
    },
  };
}

export function submitUpdateEpisodeFormEvent() {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-partner-analytics-submit',
        payload: {
          target: 'Update Episode Form',
        },
      },
    },
  };
}

export function submitCreateEpisodeFormEvent() {
  return {
    type: '', // no actual actions
    meta: {
      analytics: {
        type: 'event-partner-analytics-submit',
        payload: {
          target: 'Create Episode Form',
        },
      },
    },
  };
}
